import { Link as RouterLink, useLocation } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { styled, alpha } from '@mui/material/styles';
import { useAuth } from '../contexts/AuthContext';
import { useLogout } from '../hooks/auth';
import { Button } from './ui';
import { MobileDrawer } from './MobileDrawer';
import { AvatarDropdown } from './navigation';
import { NotificationCenter } from '../features/notifications';
import { ThemeToggle } from './ThemeToggle';
import { CitySwitcher } from './CitySwitcher';
import { Role } from '@lov/shared';
import { Logo } from './shared';

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.background.default, 0.95),
  color: theme.palette.text.primary,
  backdropFilter: 'blur(10px)',
  WebkitBackdropFilter: 'blur(10px)',
  borderBottom: `1px solid ${theme.palette.surface.glassBorder}`,
  boxShadow: 'none',
}));

const LogoLink = styled(RouterLink)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  textDecoration: 'none',
  '&:hover': {
    '& .logo-icon': {
      transform: 'rotate(-10deg) scale(1.1)',
    },
  },
}));

const LogoText = styled(Typography)(({ theme }) => ({
  fontFamily: '"IBM Plex Mono", monospace',
  fontWeight: 600,
  fontSize: '1.125rem',
  letterSpacing: '-0.02em',
  color: theme.palette.text.primary,
}));

/**
 * Navbar component for Vendor and Admin roles
 * Customers use CustomerNavbar via CustomerLayout
 *
 * This navbar is minimal - just logo and avatar dropdown
 * All navigation links are in the Sidebar for Vendor/Admin
 */
export function Navbar() {
  const { isAuthenticated, user } = useAuth();
  const { mutate: logout, isPending } = useLogout();
  const { pathname } = useLocation();

  return (
    <StyledAppBar position="sticky">
      <Toolbar
        sx={{
          minHeight: { xs: 64, md: 72 },
          px: { xs: 2, md: 3 },
        }}
      >
        {/* Logo */}
        <LogoLink to="/">
          <Logo size="medium" className="logo-icon" />
          <LogoText variant="h6">Lord of Venues</LogoText>
        </LogoLink>

        <Box sx={{ flexGrow: 1 }} />

        {/* Desktop Navigation - Simplified: Just avatar dropdown */}
        <Box
          sx={{
            display: { xs: 'none', md: 'flex' },
            alignItems: 'center',
            gap: 2,
          }}
        >
          {isAuthenticated && user ? (
            <>
              {user.role === Role.CUSTOMER && <CitySwitcher />}
              <ThemeToggle size="medium" />
              <NotificationCenter />
              <AvatarDropdown user={user} onLogout={() => logout()} isLoggingOut={isPending} />
            </>
          ) : (
            <>
              <CitySwitcher />
              <ThemeToggle size="medium" />
              {pathname !== '/login' && pathname !== '/register' && (
                <>
                  <Button variant="ghost" size="sm" component={RouterLink} to="/login">
                    Log In
                  </Button>
                  <Button variant="primary" size="sm" component={RouterLink} to="/register">
                    Sign Up
                  </Button>
                </>
              )}
            </>
          )}
        </Box>

        {/* Mobile Navigation - Hamburger drawer for Vendor/Admin */}
        <MobileDrawer
          isAuthenticated={isAuthenticated}
          user={user}
          onLogout={() => logout()}
          isLoggingOut={isPending}
        />
      </Toolbar>
    </StyledAppBar>
  );
}
